import { Select, Tag } from 'antd';
import FormItem from 'antd/lib/form/FormItem';
import TextArea from 'antd/lib/input/TextArea';
import type React from 'react';
import { useMemo } from 'react';
import {
  canShowRecallTransition,
  filterStatusTransitionsHideRecallUnlessBlockedDriver,
  RECALL_STATUS,
} from '@/core/utils/order_status_validation';
import type {
  GetMetaQuery,
  GetOrderByIdWithDriversWarehousesQuery,
} from '@/generated/graphql/graphql';

const HIDDEN_STYLE: React.CSSProperties = { display: 'none' };
const DRIVER_ROW_STYLE: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8 };
const SELECT_STYLE: React.CSSProperties = { width: 200 };

interface OrderStatusSectionProps {
  orderData: NonNullable<GetOrderByIdWithDriversWarehousesQuery['order_by_pk']>;
  driversData?: GetMetaQuery;
  onDriverChange: (driverId: string) => void;
  onStatusChange: (statusName: string) => void;
  onReasonChange: (reason: string) => void;
  isDisabled?: boolean;
  isDriverBlocked?: boolean;
  hasDriverChanged?: boolean;
  hasStatusChanged?: boolean;
  initialStatusName?: string | null;
}

export const OrderStatusSection: React.FC<OrderStatusSectionProps> = ({
  orderData,
  driversData,
  onDriverChange,
  onStatusChange,
  onReasonChange,
  isDisabled = false,
  isDriverBlocked = false,
  hasDriverChanged = false,
  hasStatusChanged = false,
  initialStatusName,
}) => {
  const driverOptions = useMemo(() => {
    const allDriverOptions =
      driversData?.user.map((driver) => (
        <Select.Option value={driver.id} key={driver.id}>
          {driver.name}
        </Select.Option>
      )) || [];

    const currentDriverId = orderData.driver?.id;
    const currentDriverInList = currentDriverId
      ? driversData?.user.some((driver) => String(driver.id) === String(currentDriverId))
      : false;

    if (orderData.driver && !currentDriverInList) {
      const driverName = orderData.driver.name || 'Unknown driver';
      const isBlocked = orderData.driver.blocked;
      const externalDriverOption = (
        <Select.Option
          key={orderData.driver.id}
          value={orderData.driver.id}
          disabled
          style={isBlocked ? undefined : HIDDEN_STYLE}
        >
          {driverName}
        </Select.Option>
      );
      return [externalDriverOption, ...allDriverOptions];
    }

    return allDriverOptions;
  }, [driversData, orderData.driver]);

  const eligibilityStatusName = initialStatusName ?? orderData.status?.name;

  const showRecallOption = useMemo(() => {
    if (!isDriverBlocked || hasDriverChanged || hasStatusChanged) return false;
    return canShowRecallTransition(eligibilityStatusName);
  }, [isDriverBlocked, hasDriverChanged, hasStatusChanged, eligibilityStatusName]);

  const rawStatusTransitions = orderData.status?.status_transitions ?? [];
  const visibleStatusTransitions = useMemo(
    () =>
      filterStatusTransitionsHideRecallUnlessBlockedDriver(
        rawStatusTransitions,
        isDriverBlocked,
        orderData.status?.name
      ),
    [rawStatusTransitions, isDriverBlocked, orderData.status?.name]
  );

  const hasRecallTransition = useMemo(
    () => visibleStatusTransitions.some((t) => t.next === RECALL_STATUS),
    [visibleStatusTransitions]
  );

  const showRecallSelectOption = showRecallOption && !hasRecallTransition;

  return (
    <>
      <FormItem className="app-form-item" label="Driver">
        <div style={DRIVER_ROW_STYLE}>
          <Select
            style={SELECT_STYLE}
            value={orderData.driver?.id || undefined}
            onChange={onDriverChange}
            placeholder="Select driver"
            showSearch
            optionFilterProp="children"
            disabled={isDisabled}
          >
            {driverOptions}
          </Select>
          {isDriverBlocked && !hasDriverChanged && <Tag color="red">Blocked</Tag>}
        </div>
      </FormItem>

      <FormItem className="app-form-item" label="Status">
        <Select
          style={{ width: 200 }}
          value={orderData.status?.name || '---'}
          onChange={onStatusChange}
          disabled={isDisabled}
        >
          {visibleStatusTransitions.map((transition) => (
            <Select.Option value={transition.next} key={transition.next}>
              {transition.next}
            </Select.Option>
          ))}
          {showRecallSelectOption && (
            <Select.Option value={RECALL_STATUS} key={RECALL_STATUS}>
              {RECALL_STATUS}
            </Select.Option>
          )}
        </Select>
      </FormItem>

      {orderData.status?.name === 'Refuse' && (
        <FormItem className="app-form-item" label="Refuse reason">
          <TextArea
            autoSize={{ minRows: 1, maxRows: 3 }}
            placeholder="Refuse comment"
            value={orderData.reasonRefusal || ''}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onReasonChange(e.target.value)}
            disabled={isDisabled}
          />
        </FormItem>
      )}
    </>
  );
};
